package com.example.mystybag.appCore.views.pages

import androidx.compose.foundation.Image
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CheckboxDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import com.example.mystybag.appCore.logic.pages.AddBagViewModel
import com.example.mystybag.appCore.model.pages.AddBagRequiredField
import com.example.mystybag.appCore.model.pages.AddBagSectionTab
import com.example.mystybag.appCore.model.pages.AddBagUIState
import com.example.mystybag.sharedmodels.project.responseModels.merchant.BagProductSelectionMode
import com.example.mystybag.common.extensions.scrollEdgeFade
import com.example.mystybag.common.ui.components.native.CircularQuantityButton
import com.example.mystybag.common.ui.components.native.CaptionCapitalization
import com.example.mystybag.common.ui.components.native.DefaultButton
import com.example.mystybag.common.ui.components.native.DefaultButtonType
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.common.ui.components.custom.ComboBox
import com.example.mystybag.common.ui.components.custom.AmountInputField
import com.example.mystybag.common.ui.components.custom.DefaultCircleShapedTextField
import com.example.mystybag.common.ui.components.custom.DatePicker
import com.example.mystybag.common.ui.components.custom.TimePickerField
import com.example.mystybag.common.resources.SR
import com.example.mystybag.common.ui.views.BottomSheetUIView
import com.example.mystybag.core.expected.rememberIsWebDesktopLayout
import com.example.mystybag.core.expected.setSoftInputModeOverlay
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.ui.theme.LocalTheme.p1
import com.example.mystybag.core.ui.theme.LocalTheme.p6
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import com.example.mystybag.core.ui.theme.LocalTheme.p12
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p24
import com.example.mystybag.core.resources.SRC
import com.example.mystybag.core.ui.theme.LocalTheme.p20
import com.example.mystybag.core.ui.theme.LocalTheme.p40
import com.example.mystybag.core.ui.theme.LocalTheme.p56
import com.example.mystybag.core.views.AppTheme
import dev.icerock.moko.resources.ImageResource
import dev.icerock.moko.resources.compose.painterResource
import dev.icerock.moko.resources.compose.stringResource
import kotlinx.datetime.Clock
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlinx.coroutines.delay

private const val FILTER_PRICE_MAX = 999.99

/** Limits price input to at most 2 decimal places (EUR). */
fun sanitizePriceInput(input: String): String {
    if (input.isEmpty()) return ""
    val noSpaces = input.replace(" ", "").replace("\u00A0", "")
    val normalized = noSpaces.replace(',', '.')
    val filtered = normalized.filter { it.isDigit() || it == '.' }
    val parts = filtered.split('.')
    return when {
        parts.size == 1 -> {
            val integerPart = parts[0]
            if (integerPart.isEmpty()) ""
            else integerPart.trimStart('0').ifEmpty { "0" }
        }
        else -> {
            val integerPart = parts[0]
            val decimalPart = parts[1].take(2)
            when {
                integerPart.isEmpty() && decimalPart.isEmpty() -> ""
                integerPart.isEmpty() -> "0.$decimalPart"
                else -> {
                    val cleanedInteger = integerPart.trimStart('0').ifEmpty { "0" }
                    "$cleanedInteger.$decimalPart"
                }
            }
        }
    }
}

/** Figma Section Header: 12sp, line height 16, letter spacing 4%, On Surface Variant; 12dp gap; 1px line. */
@Composable
private fun AddBagSectionHeader(title: String) {
    Column(modifier = Modifier.padding(top = p8).fillMaxWidth()) {
        Texts.BodySmall(
            text = title,
            color = AppTheme.current.colors.onSurfaceVariant,
            modifier = Modifier.fillMaxWidth()
        )
        Spacer(modifier = Modifier.height(p8))
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(p1)
                .background(AppTheme.current.colors.outlineVariant)
        )
    }
}

/** Figma Chip: cornerRadius 57, padding 20h 12v, label 16px. Selected: bg primaryContainer, text white. Unselected: bg white, text primaryContainer, border onSurfaceVariant. */
@Composable
private fun AddBagDiscountChip(
    percent: Int,
    selected: Boolean,
    onClick: () -> Unit
) {
    val colors = AppTheme.current.colors
    val backgroundColor = if (selected) colors.inversePrimary else Color.White
    val textColor = if (selected) Color.White else colors.inversePrimary
    val borderModifier = if (!selected) {
        Modifier.border(1.dp, colors.onSurfaceVariant, RoundedCornerShape(57.dp))
    } else Modifier
    Box(
        modifier = borderModifier
            .then(
                Modifier
                    .clip(RoundedCornerShape(57.dp))
                    .background(backgroundColor)
                    .clickable(onClick = onClick)
                    .padding(horizontal = p12, vertical = p8)
            )
    ) {
        Texts.BodyLarge(
            text = "$percent%",
            color = textColor
        )
    }
}

private val AddBagAccordionHeaderHeight = 56.dp

@Composable
private fun AddBagAccordionHeader(
    tab: AddBagSectionTab,
    label: String,
    icon: ImageResource,
    expanded: Boolean,
    tabsWithErrors: Set<AddBagSectionTab>,
    onTabSelected: (AddBagSectionTab) -> Unit,
    subtitle: String? = null
) {
    val colors = AppTheme.current.colors
    val primaryVariant = colors.inversePrimary
    val hasError = tab in tabsWithErrors
    val titleColor = when {
        hasError -> colors.error
        expanded -> primaryVariant
        else -> colors.onSurface
    }
    val iconColor = when {
        hasError -> colors.error
        expanded -> primaryVariant
        else -> colors.onSurfaceVariant
    }
    val chevronColor = when {
        hasError -> colors.error
        else -> primaryVariant
    }
    val arrowRotation by animateFloatAsState(
        targetValue = if (expanded) 180f else 0f,
        animationSpec = tween(durationMillis = 200),
        label = "addBagAccordionArrowRotation"
    )
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .height(AddBagAccordionHeaderHeight)
            .clip(RoundedCornerShape(p20))
            .background(colors.surfaceContainerLowest)
            .clickable(
                interactionSource = remember { MutableInteractionSource() },
                indication = null
            ) { onTabSelected(tab) }
            .padding(horizontal = p16),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Image(
            painter = painterResource(icon),
            contentDescription = null,
            modifier = Modifier.size(24.dp),
            colorFilter = ColorFilter.tint(iconColor)
        )
        Spacer(modifier = Modifier.width(p12))
        Column(
            modifier = Modifier
                .weight(1f)
                .padding(end = p8)
        ) {
            Texts.TitleMedium(text = label, color = titleColor)
            if (!subtitle.isNullOrBlank()) {
                Texts.BodySmall(text = subtitle, color = colors.onSurfaceVariant)
            }
        }
        Image(
            painter = painterResource(SRC.images.combobox_arrow_down_green),
            contentDescription = null,
            modifier = Modifier
                .size(p16)
                .graphicsLayer { rotationZ = arrowRotation },
            colorFilter = ColorFilter.tint(chevronColor)
        )
    }
}

@Composable
fun AddBagView(viewModel: AddBagViewModel) {
    DisposableEffect(Unit) {
        setSoftInputModeOverlay(overlay = true)
        onDispose { setSoftInputModeOverlay(overlay = false) }
    }
    LaunchedEffect(Unit) {
        delay(150)
        setSoftInputModeOverlay(overlay = true)
    }

    if (rememberIsWebDesktopLayout()) {
        AddBagWebView(viewModel)
        return
    }

    BottomSheetUIView(
        viewModel = viewModel,
        modifier = Modifier.fillMaxSize(),
        applyBottomInsetSpacer = false,
        bottomSheetFactory = {}
    ) { _ ->
        AddBagScreenBody(
            viewModel = viewModel,
            columnModifier = Modifier.fillMaxSize(),
            isWebForm = false
        )
    }
}

/**
 * Bag form body. [isWebForm] enables the Web-only accordion chrome (location-edit style) and save button row;
 * mobile keeps the previous single-column layout.
 */
@Composable
internal fun AddBagScreenBody(
    viewModel: AddBagViewModel,
    columnModifier: Modifier,
    isWebForm: Boolean
) {
    val uiState by viewModel.uiState.subscribeAsState()
    val todayInstant = Clock.System.now()
    val today = todayInstant.toLocalDateTime(TimeZone.currentSystemDefault())
    val todayDate = LocalDate(today.year, today.monthNumber, today.dayOfMonth)
    val discountPercentForCalc = viewModel.getDiscountPercentageInt(uiState) ?: 30
    val computedPrice = viewModel.getComputedPriceWithDiscount(uiState.priceNormal, discountPercentForCalc)
    val computedPriceText = if (computedPrice != null) {
        com.example.mystybag.core.expected.format("%.2f", computedPrice)
    } else ""

    val scrollState = rememberScrollState()
    var selectedTab by rememberSaveable { mutableStateOf(AddBagSectionTab.DETAILS) }
    var expandedTab by rememberSaveable { mutableStateOf<AddBagSectionTab?>(AddBagSectionTab.DETAILS) }
    val tabsWithErrors = remember(uiState.requiredFieldErrors) {
        uiState.requiredFieldErrors.map { field ->
            when (field) {
                AddBagRequiredField.PRICE_NORMAL,
                AddBagRequiredField.DISCOUNT_PERCENTAGE -> AddBagSectionTab.PRICE
                AddBagRequiredField.PICKUP_TIME_FROM,
                AddBagRequiredField.PICKUP_TIME_TO -> AddBagSectionTab.COLLECT_TIME
                else -> AddBagSectionTab.DETAILS
            }
        }.toSet()
    }
    val titleFocusRequester = remember { FocusRequester() }
    val bagsCountFocusRequester = remember { FocusRequester() }
    val priceNormalFocusRequester = remember { FocusRequester() }
    val discountFocusRequester = remember { FocusRequester() }
    LaunchedEffect(uiState.validationRequestId) {
        if (uiState.validationRequestId == 0) return@LaunchedEffect
        uiState.validationTargetTab?.let {
            selectedTab = it
            expandedTab = it
        }
        delay(120)
        when (uiState.validationTargetField) {
            AddBagRequiredField.TITLE -> titleFocusRequester.requestFocus()
            AddBagRequiredField.BAGS_COUNT -> bagsCountFocusRequester.requestFocus()
            AddBagRequiredField.PRICE_NORMAL -> priceNormalFocusRequester.requestFocus()
            AddBagRequiredField.DISCOUNT_PERCENTAGE -> discountFocusRequester.requestFocus()
            else -> Unit
        }
    }

    Column(
        modifier = columnModifier.then(
            if (!isWebForm) Modifier.padding(horizontal = p16) else Modifier
        )
    ) {
        if (isWebForm) {
            BoxWithConstraints(
                modifier = Modifier
                    .weight(1f)
                    .fillMaxWidth()
            ) {
                val scrollViewportHeight = maxHeight
                Column(
                    modifier = Modifier
                        .height(scrollViewportHeight)
                        .fillMaxWidth()
                        .verticalScroll(scrollState)
                        .scrollEdgeFade(showTop = false)
                        .padding(top = p20, bottom = p16)
                ) {
                    val accordionSections = listOf(
                        Triple(AddBagSectionTab.DETAILS, stringResource(SR.strings.bag_details), SRC.images.add_bag_shopping_bag),
                        Triple(AddBagSectionTab.PRICE, stringResource(SR.strings.section_price), SRC.images.add_bag_tag),
                        Triple(AddBagSectionTab.COLLECT_TIME, stringResource(SR.strings.collect_time), SRC.images.add_bag_clock),
                        Triple(AddBagSectionTab.PRODUCTS, stringResource(SR.strings.products), SRC.images.add_bag_shopping_bag)
                    )
                    accordionSections.forEach { (tab, label, icon) ->
                        Box(modifier = Modifier.padding(horizontal = p16)) {
                            AddBagAccordionHeader(
                                tab = tab,
                                label = label,
                                icon = icon,
                                expanded = expandedTab == tab,
                                tabsWithErrors = tabsWithErrors,
                                onTabSelected = { clicked ->
                                    selectedTab = clicked
                                    expandedTab = if (expandedTab == clicked) null else clicked
                                },
                                subtitle = if (tab == AddBagSectionTab.PRODUCTS) bagProductsSummary(uiState) else null
                            )
                        }
                        if (expandedTab == tab) {
                            Spacer(modifier = Modifier.height(p12))
                            Column(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(horizontal = p16)
                            ) {
                                when (tab) {
                                    AddBagSectionTab.DETAILS -> AddBagDetailsContent(
                                        uiState = uiState,
                                        viewModel = viewModel,
                                        todayDate = todayDate,
                                        titleFocusRequester = titleFocusRequester,
                                        bagsCountFocusRequester = bagsCountFocusRequester
                                    )

                                    AddBagSectionTab.PRICE -> AddBagPriceContent(
                                        uiState = uiState,
                                        viewModel = viewModel,
                                        computedPriceText = computedPriceText,
                                        priceNormalFocusRequester = priceNormalFocusRequester,
                                        discountFocusRequester = discountFocusRequester
                                    )

                                    AddBagSectionTab.COLLECT_TIME -> AddBagCollectTimeContent(
                                        uiState = uiState,
                                        viewModel = viewModel
                                    )

                                    AddBagSectionTab.PRODUCTS -> AddBagProductsContent(
                                        uiState = uiState,
                                        viewModel = viewModel
                                    )
                                }
                            }
                            Spacer(modifier = Modifier.height(p16))
                        } else {
                            Spacer(modifier = Modifier.height(p8))
                        }
                    }
                }
            }
        } else {
            Column(
                modifier = Modifier
                    .weight(1f)
                    .fillMaxWidth()
                    .verticalScroll(scrollState)
                    .scrollEdgeFade(showTop = false)
                    .padding(top = p20, bottom = p16)
            ) {
                val accordionSections = listOf(
                    Triple(AddBagSectionTab.DETAILS, stringResource(SR.strings.bag_details), SRC.images.add_bag_shopping_bag),
                    Triple(AddBagSectionTab.PRICE, stringResource(SR.strings.section_price), SRC.images.add_bag_tag),
                    Triple(AddBagSectionTab.COLLECT_TIME, stringResource(SR.strings.collect_time), SRC.images.add_bag_clock),
                    Triple(AddBagSectionTab.PRODUCTS, stringResource(SR.strings.products), SRC.images.add_bag_shopping_bag)
                )
                accordionSections.forEach { (tab, label, icon) ->
                    AddBagAccordionHeader(
                        tab = tab,
                        label = label,
                        icon = icon,
                        expanded = expandedTab == tab,
                        tabsWithErrors = tabsWithErrors,
                        onTabSelected = { clicked ->
                            selectedTab = clicked
                            expandedTab = if (expandedTab == clicked) null else clicked
                        },
                        subtitle = if (tab == AddBagSectionTab.PRODUCTS) bagProductsSummary(uiState) else null
                    )
                    if (expandedTab == tab) {
                        Spacer(modifier = Modifier.height(p12))
                        when (tab) {
                            AddBagSectionTab.DETAILS -> AddBagDetailsContent(
                                uiState = uiState,
                                viewModel = viewModel,
                                todayDate = todayDate,
                                titleFocusRequester = titleFocusRequester,
                                bagsCountFocusRequester = bagsCountFocusRequester
                            )

                            AddBagSectionTab.PRICE -> AddBagPriceContent(
                                uiState = uiState,
                                viewModel = viewModel,
                                computedPriceText = computedPriceText,
                                priceNormalFocusRequester = priceNormalFocusRequester,
                                discountFocusRequester = discountFocusRequester
                            )

                            AddBagSectionTab.COLLECT_TIME -> AddBagCollectTimeContent(
                                uiState = uiState,
                                viewModel = viewModel
                            )

                            AddBagSectionTab.PRODUCTS -> AddBagProductsContent(
                                uiState = uiState,
                                viewModel = viewModel
                            )
                        }
                        Spacer(modifier = Modifier.height(p16))
                    } else {
                        Spacer(modifier = Modifier.height(p8))
                    }
                }
            }
        }
        if (isWebForm) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = p16, vertical = p8),
                contentAlignment = Alignment.Center
            ) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .widthIn(max = 412.dp),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    if (uiState.isLoading) {
                        Box(
                            modifier = Modifier.fillMaxWidth(),
                            contentAlignment = Alignment.Center
                        ) {
                            CircularProgressIndicator()
                        }
                    } else {
                        DefaultButton(
                            modifier = Modifier
                                .fillMaxWidth(0.5f)
                                .height(p56),
                            fillMaxWidth = false,
                            type = DefaultButtonType.PRIMARY,
                            caption = if (uiState.isEditMode) sharedString(SR.strings.save) else sharedString(SR.strings.create),
                            capitalization = CaptionCapitalization.None,
                            contentPadding = PaddingValues(horizontal = p16, vertical = 0.dp),
                            callback = { viewModel.saveBag(selectedTab) }
                        )
                    }
                }
            }
        } else {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(top = p8, bottom = p8)
            ) {
                if (uiState.isLoading) {
                    Box(
                        modifier = Modifier.fillMaxWidth(),
                        contentAlignment = Alignment.Center
                    ) {
                        CircularProgressIndicator()
                    }
                } else {
                    DefaultButton(
                        caption = if (uiState.isEditMode) sharedString(SR.strings.save) else sharedString(SR.strings.create),
                        callback = { viewModel.saveBag(selectedTab) },
                        modifier = Modifier.fillMaxWidth()
                    )
                }
            }
        }
    }
}

@Composable
private fun AddBagDetailsContent(
    uiState: AddBagUIState,
    viewModel: AddBagViewModel,
    todayDate: LocalDate,
    titleFocusRequester: FocusRequester,
    bagsCountFocusRequester: FocusRequester
) {
    val selectedLocation = uiState.availableLocations.find { it.id == uiState.merchantLocationId }
    val singleLocationOnly = uiState.availableLocations.size == 1
    val requiredErrors = uiState.requiredFieldErrors
    val colors = AppTheme.current.colors
    ComboBox(
        modifier = Modifier.fillMaxWidth(),
        value = selectedLocation,
        items = uiState.availableLocations,
        onValueChange = viewModel::selectLocation,
        label = stringResource(SR.strings.select_location),
        displayText = { it.title.orEmpty().ifEmpty { it.address.orEmpty().ifEmpty { it.id.toString() } } },
        useRightArrow = false,
        enabled = !singleLocationOnly,
        isError = AddBagRequiredField.LOCATION in requiredErrors,
        backgroundColor = if (singleLocationOnly) colors.surfaceContainerLowest else colors.surface,
        borderColor = colors.outline,
        disabledTextColor = colors.onSurface
    )

    Spacer(modifier = Modifier.height(p16))

    DefaultCircleShapedTextField(
        value = uiState.title,
        onValueChange = viewModel::updateTitle,
        label = stringResource(SR.strings.bag_title),
        modifier = Modifier.fillMaxWidth(),
        isError = AddBagRequiredField.TITLE in requiredErrors,
        errorText = "",
        focusRequester = titleFocusRequester
    )

    Spacer(modifier = Modifier.height(p16))

    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(p16)
    ) {
        DatePicker(
            modifier = Modifier.weight(1f),
            value = uiState.activeFrom,
            onValueChange = viewModel::updateActiveFrom,
            label = stringResource(SR.strings.active_from),
            enabled = true,
            minDate = if (uiState.isEditMode) null else todayDate,
            isError = AddBagRequiredField.ACTIVE_FROM in requiredErrors,
            errorText = ""
        )

        DatePicker(
            modifier = Modifier.weight(1f),
            value = uiState.activeTo,
            onValueChange = viewModel::updateActiveTo,
            label = stringResource(SR.strings.active_to),
            enabled = true,
            minDate = uiState.activeFrom,
            isError = AddBagRequiredField.ACTIVE_TO in requiredErrors || uiState.activeToError.isNotEmpty(),
            errorText = uiState.activeToError
        )
    }

    Spacer(modifier = Modifier.height(p16))

    val bagsCountError = AddBagRequiredField.BAGS_COUNT in requiredErrors
    val displayBagsCount = uiState.bagsCount.toIntOrNull()?.takeIf { it >= 1 } ?: 1
    val canDecrementBags = displayBagsCount > 1
    val canIncrementBags = displayBagsCount < 9999

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .focusRequester(bagsCountFocusRequester)
    ) {
        Texts.LabelMedium(
            text = stringResource(SR.strings.available_bags),
            color = if (bagsCountError) colors.error else colors.onSurface,
            modifier = Modifier.fillMaxWidth(),
            textAlign = TextAlign.Center
        )
        Spacer(modifier = Modifier.height(p8))
        Box(
            modifier = Modifier.fillMaxWidth(),
            contentAlignment = Alignment.Center
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(p16)
            ) {
                CircularQuantityButton(
                    symbol = "−",
                    backgroundColor = colors.surfaceContainerLow,
                    contentColor = colors.onSurfaceVariant,
                    enabled = canDecrementBags,
                    onClick = viewModel::decrementBagsCount,
                    buttonSize = p40
                )
                Texts.TitleLarge(
                    text = displayBagsCount.toString(),
                    color = colors.onSurface
                )
                CircularQuantityButton(
                    symbol = "+",
                    backgroundColor = colors.primaryContainer,
                    contentColor = colors.onPrimaryContainer,
                    enabled = canIncrementBags,
                    onClick = viewModel::incrementBagsCount,
                    buttonSize = p40
                )
            }
        }
    }

}

@Composable
private fun AddBagCollectTimeContent(
    uiState: AddBagUIState,
    viewModel: AddBagViewModel
) {
    val requiredErrors = uiState.requiredFieldErrors
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(p16)
    ) {
        TimePickerField(
            modifier = Modifier.weight(1f),
            value = uiState.pickupTimeFrom,
            onValueChange = viewModel::updatePickupTimeFrom,
            label = stringResource(SR.strings.time_from),
            enabled = true,
            isError = AddBagRequiredField.PICKUP_TIME_FROM in requiredErrors,
            errorText = ""
        )
        TimePickerField(
            modifier = Modifier.weight(1f),
            value = uiState.pickupTimeTo,
            onValueChange = viewModel::updatePickupTimeTo,
            label = stringResource(SR.strings.time_to),
            enabled = true,
            isError = AddBagRequiredField.PICKUP_TIME_TO in requiredErrors,
            errorText = ""
        )
    }
}

@Composable
private fun AddBagPriceContent(
    uiState: AddBagUIState,
    viewModel: AddBagViewModel,
    computedPriceText: String,
    priceNormalFocusRequester: FocusRequester,
    discountFocusRequester: FocusRequester
) {
    val requiredErrors = uiState.requiredFieldErrors
    AmountInputField(
        value = uiState.priceNormal,
        onValueChange = { newText ->
            val filtered = sanitizePriceInput(newText)
            val parsed = filtered.toDoubleOrNull()
            if (parsed != null && parsed > FILTER_PRICE_MAX) {
                viewModel.showPriceMaxExceededMessage()
            } else {
                viewModel.updatePriceNormal(filtered)
            }
        },
        placeholder = "0.00",
        label = stringResource(SR.strings.price_normal),
        modifier = Modifier.fillMaxWidth(),
        isError = AddBagRequiredField.PRICE_NORMAL in requiredErrors,
        focusRequester = priceNormalFocusRequester,
        onFocusChanged = { focused ->
            if (focused) viewModel.onPriceNormalFocusGained()
            else viewModel.onPriceNormalFocusLost()
        }
    )

    Spacer(modifier = Modifier.height(p16))

    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(p16)
    ) {
        DefaultCircleShapedTextField(
            value = "${uiState.discountPercentageInput} %",
            onValueChange = viewModel::updateDiscountPercentage,
            label = stringResource(SR.strings.discount_percentage),
            modifier = Modifier.weight(1f),
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
            textAlign = TextAlign.End,
            isError = AddBagRequiredField.DISCOUNT_PERCENTAGE in requiredErrors || uiState.discountPercentageError.isNotEmpty(),
            errorText = uiState.discountPercentageError,
            hasEndPadding = false,
            focusRequester = discountFocusRequester
        )
        DefaultCircleShapedTextField(
            value = (if (uiState.isEditMode) uiState.priceWithDiscountLoaded else null) ?: computedPriceText,
            onValueChange = {},
            label = stringResource(SR.strings.price_with_discount),
            modifier = Modifier.weight(1f),
            enabled = false,
            readOnly = true,
            textAlign = TextAlign.End,
            leadingIcon = {
                Texts.BodyLarge(
                    text = uiState.currency,
                    color = AppTheme.current.colors.onSurface,
                    modifier = Modifier.pointerInput(Unit) {}
                )
            }
        )
    }

    Spacer(modifier = Modifier.height(p16))

    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(p6)
    ) {
        listOf(30, 40, 45, 50, 60).forEach { percent ->
            AddBagDiscountChip(
                percent = percent,
                selected = uiState.discountPercentageInput == percent.toString(),
                onClick = { viewModel.setDiscountPercentage(percent) }
            )
        }
    }
}

/** Bag products section: pick all products, a category, or a custom set of products. */
@Composable
private fun AddBagProductsContent(
    uiState: AddBagUIState,
    viewModel: AddBagViewModel
) {
    val colors = AppTheme.current.colors

    if (uiState.availableProducts.isEmpty() && !uiState.isLoadingProducts) {
        Texts.BodyMedium(
            text = stringResource(SR.strings.no_products),
            color = colors.onSurfaceVariant,
            modifier = Modifier
                .fillMaxWidth()
                .padding(vertical = p8)
        )
        return
    }

    Texts.BodySmall(
        text = stringResource(SR.strings.product_selection_title),
        color = colors.onSurfaceVariant,
        modifier = Modifier.fillMaxWidth()
    )
    Spacer(modifier = Modifier.height(p12))

    // Three selection-mode cards. The bag flow only selects; products/categories
    // are created in the location context, so there are no manage/add actions here.
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(p8)
    ) {
        AddBagProductModeCard(
            icon = SRC.images.home_view_grid,
            title = stringResource(SR.strings.product_mode_all_title),
            subtitle = stringResource(SR.strings.product_mode_all_subtitle),
            selected = uiState.productSelectionMode == BagProductSelectionMode.ALL,
            onClick = { viewModel.setProductSelectionMode(BagProductSelectionMode.ALL) },
            modifier = Modifier.weight(1f)
        )
        AddBagProductModeCard(
            icon = SRC.images.location_tab_menu_icon,
            title = stringResource(SR.strings.product_mode_category_title),
            subtitle = stringResource(SR.strings.product_mode_category_subtitle),
            selected = uiState.productSelectionMode == BagProductSelectionMode.CATEGORY,
            onClick = { viewModel.setProductSelectionMode(BagProductSelectionMode.CATEGORY) },
            modifier = Modifier.weight(1f)
        )
        AddBagProductModeCard(
            icon = SRC.images.select_icon,
            title = stringResource(SR.strings.product_mode_custom_title),
            subtitle = stringResource(SR.strings.product_mode_custom_subtitle),
            selected = uiState.productSelectionMode == BagProductSelectionMode.CUSTOM,
            onClick = { viewModel.setProductSelectionMode(BagProductSelectionMode.CUSTOM) },
            modifier = Modifier.weight(1f)
        )
    }

    when (uiState.productSelectionMode) {
        BagProductSelectionMode.ALL -> {
            Spacer(modifier = Modifier.height(p16))
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(p12))
                    .border(1.dp, colors.outlineVariant, RoundedCornerShape(p12))
                    .padding(horizontal = p16, vertical = p20),
                contentAlignment = Alignment.Center
            ) {
                Texts.BodyMedium(
                    text = stringResource(SR.strings.product_all_auto_note),
                    color = colors.onSurfaceVariant,
                    textAlign = TextAlign.Center,
                    modifier = Modifier.fillMaxWidth()
                )
            }
        }

        BagProductSelectionMode.CATEGORY -> {
            Spacer(modifier = Modifier.height(p16))
            Texts.BodySmall(
                text = "${stringResource(SR.strings.select_product_category)} (${uiState.selectedProductCategoryIds.size})",
                color = colors.onSurfaceVariant,
                modifier = Modifier.fillMaxWidth()
            )
            Spacer(modifier = Modifier.height(p8))
            if (uiState.availableProductCategories.isEmpty()) {
                Texts.BodyMedium(
                    text = stringResource(SR.strings.no_product_categories),
                    color = colors.onSurfaceVariant,
                    modifier = Modifier.fillMaxWidth().padding(vertical = p8)
                )
            } else {
                val depthById = remember(uiState.availableProductCategories) {
                    computeCategoryDepths(uiState.availableProductCategories)
                }
                val counts = remember(uiState.availableProductCategories, uiState.availableProducts) {
                    computeBagProductCounts(uiState.availableProductCategories, uiState.availableProducts)
                }
                // Show the same tree order as the location's catalog: each parent
                // followed by its own subgroups (sorted by sortOrder), so same-named
                // subgroups from different days don't get clustered together.
                val orderedCategories = remember(uiState.availableProductCategories) {
                    orderCategoriesHierarchically(uiState.availableProductCategories)
                }
                orderedCategories.forEach { category ->
                    val isSelected = uiState.selectedProductCategoryIds.contains(category.id)
                    val depth = depthById[category.id] ?: 0
                    val count = counts[category.id] ?: 0
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .clip(RoundedCornerShape(p12))
                            .then(
                                if (isSelected) Modifier.border(1.dp, colors.primary, RoundedCornerShape(p12))
                                else Modifier.border(1.dp, colors.outline, RoundedCornerShape(p12))
                            )
                            .clickable { viewModel.toggleProductCategory(category.id) }
                            .padding(start = p16 + (p16 * depth), end = p12, top = p12, bottom = p12),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        SelectionCircle(selected = isSelected)
                        Spacer(modifier = Modifier.width(p12))
                        Column(modifier = Modifier.weight(1f)) {
                            Texts.BodyLarge(text = category.name.ifEmpty { "-" }, color = colors.onSurface)
                            Texts.BodySmall(
                                text = "$count ${stringResource(SR.strings.products_count_suffix)}",
                                color = colors.onSurfaceVariant
                            )
                        }
                        CountBadge(count = count)
                    }
                    Spacer(modifier = Modifier.height(p8))
                }
            }
        }

        BagProductSelectionMode.CUSTOM -> {
            Spacer(modifier = Modifier.height(p16))
            Texts.BodySmall(
                text = "${stringResource(SR.strings.products)} (${uiState.selectedProductIds.size})",
                color = colors.onSurfaceVariant,
                modifier = Modifier.fillMaxWidth()
            )
            Spacer(modifier = Modifier.height(p8))
            DefaultCircleShapedTextField(
                value = uiState.productSearchQuery,
                onValueChange = viewModel::updateProductSearchQuery,
                singleLine = true,
                placeholder = {
                    Texts.BodyMedium(
                        text = stringResource(SR.strings.search_product_hint),
                        color = colors.onSurfaceVariant
                    )
                },
                leadingIcon = {
                    Image(
                        painter = painterResource(SRC.images.search_icon),
                        contentDescription = null,
                        colorFilter = ColorFilter.tint(colors.onSurfaceVariant),
                        modifier = Modifier.size(20.dp)
                    )
                },
                modifier = Modifier.fillMaxWidth()
            )
            Spacer(modifier = Modifier.height(p12))

            val query = uiState.productSearchQuery.trim()
            val categoryNameById = remember(uiState.availableProductCategories) {
                uiState.availableProductCategories.associate { it.id to it.name }
            }
            val visibleProducts = if (query.isEmpty()) uiState.availableProducts
            else uiState.availableProducts.filter { it.name.contains(query, ignoreCase = true) }

            if (visibleProducts.isEmpty()) {
                Texts.BodyMedium(
                    text = stringResource(SR.strings.no_products),
                    color = colors.onSurfaceVariant,
                    modifier = Modifier.fillMaxWidth().padding(vertical = p8)
                )
            } else {
                visibleProducts.forEach { product ->
                    val isSelected = uiState.selectedProductIds.contains(product.id)
                    val categoryName = product.categoryId?.let { categoryNameById[it] }
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .clip(RoundedCornerShape(p12))
                            .then(
                                if (isSelected) Modifier
                                    .background(colors.primary.copy(alpha = 0.08f))
                                    .border(1.dp, colors.primary, RoundedCornerShape(p12))
                                else Modifier.border(1.dp, colors.outline, RoundedCornerShape(p12))
                            )
                            .clickable { viewModel.toggleProduct(product.id) }
                            .padding(horizontal = p12, vertical = p12),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        SelectionCircle(selected = isSelected)
                        Spacer(modifier = Modifier.width(p12))
                        Column(modifier = Modifier.weight(1f)) {
                            Texts.BodyLarge(text = product.name.ifEmpty { "-" }, color = colors.onSurface)
                            if (!categoryName.isNullOrBlank()) {
                                Texts.BodySmall(text = categoryName, color = colors.onSurfaceVariant)
                            }
                        }
                    }
                    Spacer(modifier = Modifier.height(p8))
                }
            }
        }
    }

    Spacer(modifier = Modifier.height(p24))
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(p12))
            .clickable { viewModel.setContainsProductsForPersonsOver18(!uiState.containsProductsForPersonsOver18) }
            .padding(vertical = p8),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(p12)
    ) {
        Checkbox(
            checked = uiState.containsProductsForPersonsOver18,
            onCheckedChange = viewModel::setContainsProductsForPersonsOver18,
            colors = CheckboxDefaults.colors(
                checkedColor = colors.primary,
                uncheckedColor = colors.outline
            )
        )
        Texts.BodyMedium(
            text = stringResource(SR.strings.bag_contains_products_for_persons_over_18),
            color = colors.onSurface,
            modifier = Modifier.weight(1f)
        )
    }
}

@Composable
private fun bagProductsSummary(uiState: AddBagUIState): String = when (uiState.productSelectionMode) {
    BagProductSelectionMode.ALL -> stringResource(SR.strings.product_selection_all)
    BagProductSelectionMode.CATEGORY -> {
        val n = uiState.selectedProductCategoryIds.size
        "$n ${stringResource(SR.strings.categories_count_suffix)}"
    }
    BagProductSelectionMode.CUSTOM ->
        "${uiState.selectedProductIds.size} ${stringResource(SR.strings.selected_products_suffix)}"
}

@Composable
private fun AddBagProductModeCard(
    icon: ImageResource,
    title: String,
    subtitle: String,
    selected: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    val colors = AppTheme.current.colors
    Box(modifier = modifier) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .clip(RoundedCornerShape(p16))
                .then(
                    if (selected) Modifier
                        .background(colors.primary.copy(alpha = 0.08f))
                        .border(1.dp, colors.primary, RoundedCornerShape(p16))
                    else Modifier.border(1.dp, colors.outline, RoundedCornerShape(p16))
                )
                .clickable(onClick = onClick)
                .padding(p12)
        ) {
            Box(
                modifier = Modifier
                    .size(36.dp)
                    .clip(CircleShape)
                    .background(if (selected) colors.primary else colors.surfaceContainerLowest),
                contentAlignment = Alignment.Center
            ) {
                Image(
                    painter = painterResource(icon),
                    contentDescription = null,
                    colorFilter = ColorFilter.tint(if (selected) Color.White else colors.onSurfaceVariant),
                    modifier = Modifier.size(18.dp)
                )
            }
            Spacer(modifier = Modifier.height(p8))
            Texts.BodyLarge(text = title, color = colors.onSurface)
            Texts.BodySmall(text = subtitle, color = colors.onSurfaceVariant)
        }
        if (selected) {
            Box(
                modifier = Modifier
                    .align(Alignment.TopEnd)
                    .padding(p8)
                    .size(20.dp)
                    .clip(CircleShape)
                    .background(colors.primary),
                contentAlignment = Alignment.Center
            ) {
                Image(
                    painter = painterResource(SRC.images.check_fill_icon),
                    contentDescription = null,
                    colorFilter = ColorFilter.tint(Color.White),
                    modifier = Modifier.size(12.dp)
                )
            }
        }
    }
}

@Composable
private fun SelectionCircle(selected: Boolean) {
    val colors = AppTheme.current.colors
    Box(
        modifier = Modifier
            .size(22.dp)
            .clip(CircleShape)
            .then(
                if (selected) Modifier.background(colors.primary)
                else Modifier.border(2.dp, colors.outline, CircleShape)
            ),
        contentAlignment = Alignment.Center
    ) {
        if (selected) {
            Image(
                painter = painterResource(SRC.images.check_fill_icon),
                contentDescription = null,
                colorFilter = ColorFilter.tint(Color.White),
                modifier = Modifier.size(12.dp)
            )
        }
    }
}

@Composable
private fun CountBadge(count: Int) {
    val colors = AppTheme.current.colors
    Box(
        modifier = Modifier
            .clip(RoundedCornerShape(50))
            .background(colors.surfaceContainerLowest)
            .padding(horizontal = p12, vertical = p6),
        contentAlignment = Alignment.Center
    ) {
        Texts.LabelLarge(text = count.toString(), color = colors.onSurfaceVariant)
    }
}

/** Total products contained in each category, including descendant subcategories. */
private fun computeBagProductCounts(
    categories: List<com.example.mystybag.sharedmodels.project.responseModels.merchant.ProductCategoryModel>,
    products: List<com.example.mystybag.sharedmodels.project.responseModels.merchant.ProductModel>
): Map<Long, Int> {
    val childrenByParent = categories.groupBy { it.parentCategoryId }
    val directCounts = products.groupingBy { it.categoryId }.eachCount()
    val result = mutableMapOf<Long, Int>()
    fun countOf(categoryId: Long): Int {
        result[categoryId]?.let { return it }
        var total = directCounts[categoryId] ?: 0
        childrenByParent[categoryId]?.forEach { total += countOf(it.id) }
        result[categoryId] = total
        return total
    }
    categories.forEach { countOf(it.id) }
    return result
}

/**
 * Flattens categories into hierarchical (tree) order: each parent is immediately
 * followed by its descendants, every level sorted by [sortOrder] then name. This
 * mirrors the location catalog view so subgroups appear under their own parent
 * (e.g. each day's "Супи" under that day) instead of being grouped by name.
 */
private fun orderCategoriesHierarchically(
    categories: List<com.example.mystybag.sharedmodels.project.responseModels.merchant.ProductCategoryModel>
): List<com.example.mystybag.sharedmodels.project.responseModels.merchant.ProductCategoryModel> {
    val knownIds = categories.mapTo(HashSet()) { it.id }
    val childrenByParent = categories
        .filter { it.parentCategoryId != null && it.parentCategoryId in knownIds }
        .groupBy { it.parentCategoryId }
    val roots = categories
        .filter { it.parentCategoryId == null || it.parentCategoryId !in knownIds }
        .sortedWith(compareBy({ it.sortOrder }, { it.name }))
    val result = mutableListOf<com.example.mystybag.sharedmodels.project.responseModels.merchant.ProductCategoryModel>()
    val visited = HashSet<Long>()
    fun append(category: com.example.mystybag.sharedmodels.project.responseModels.merchant.ProductCategoryModel) {
        if (!visited.add(category.id)) return
        result += category
        childrenByParent[category.id]
            ?.sortedWith(compareBy({ it.sortOrder }, { it.name }))
            ?.forEach { append(it) }
    }
    roots.forEach { append(it) }
    // Defensive: append anything left unvisited (e.g. parent cycles).
    categories.filter { it.id !in visited }.forEach { result += it }
    return result
}

/** Computes the depth (indentation level) of each category from its parent chain. */
private fun computeCategoryDepths(
    categories: List<com.example.mystybag.sharedmodels.project.responseModels.merchant.ProductCategoryModel>
): Map<Long, Int> {
    val parentById = categories.associate { it.id to it.parentCategoryId }
    val depthCache = mutableMapOf<Long, Int>()
    fun depthOf(id: Long): Int {
        depthCache[id]?.let { return it }
        val parent = parentById[id]
        val depth = if (parent == null) 0 else depthOf(parent) + 1
        depthCache[id] = depth
        return depth
    }
    return categories.associate { it.id to depthOf(it.id) }
}

